import {
  configMgmtControllerListRequestTypes,
  configMgmtControllerListRiskLevels,
  requestsControllerFindAll,
} from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { Building2, Car, FileCheck, Leaf, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { DatePicker } from '../../../components/ui/date-picker';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

interface PermitTypeOption {
  id: string;
  name: string;
  code: string;
  hasRenewal: boolean;
}

interface RiskLevel {
  id: string;
  name: string;
  code: string;
  displayOrder: number;
  applyTo: 'NEW' | 'RENEWAL';
  status: string;
  totalFee: string;
}

interface VehicleLicense {
  licenseNumber: string;
  placeOfIssue: string;
}

export interface PermitDetailsData {
  permitTypeId: string;
  permitTypeCode: string;
  requestType: string;
  facilitySubType: string;
  riskCode: string;
  riskId: string;
  riskCategoryTotalFee: number;
  permissionDate: string;
  expiryDate: string;
  permitActivity: string;
  envPermitGeneralComments: string;
  licenseType: string;
  isRenewalOfId: string;
  vehicleCount: number;
  vehicleLicenses: VehicleLicense[];
  wasteClassification: string;
  wasteType: string;
  wasteQuantity: string;
  landfillLocation: string;
}

interface OriginalPermitOption {
  id: string;
  referenceNo: string;
  facilitySubType?: string | null;
  riskCode?: string | null;
}

interface Props {
  value: PermitDetailsData;
  onChange: (updates: Partial<PermitDetailsData>) => void;
  errors: Partial<Record<keyof PermitDetailsData, string>>;
  accountId?: string;
  hideTypeCards?: boolean;
}

const CODE_STYLE: Record<
  string,
  { icon: React.ElementType; iconColor: string; selectedBg: string }
> = {
  ENV: { icon: Leaf, iconColor: 'text-green-600', selectedBg: 'bg-green-600' },
  WASTE: { icon: Building2, iconColor: 'text-orange-600', selectedBg: 'bg-orange-500' },
  VEH: { icon: Car, iconColor: 'text-blue-600', selectedBg: 'bg-blue-600' },
};
const DEFAULT_STYLE = { icon: FileCheck, iconColor: 'text-brand-600', selectedBg: 'bg-brand-500' };

const LICENSE_TYPE_OPTIONS = ['Commercial', 'Technical', 'Industrial'] as const;

export function PermitDetailsStep({ value, onChange, errors, accountId, hideTypeCards }: Props) {
  const today = new Date().toISOString().split('T')[0];

  function expiryMinDate(permissionDate?: string) {
    if (permissionDate && permissionDate > today) return permissionDate;
    return today;
  }

  const { data: permitTypes } = useQuery({
    queryKey: ['permit-types'],
    queryFn: () => configMgmtControllerListRequestTypes() as unknown as Promise<PermitTypeOption[]>,
  });

  // Load risk levels for whichever type currently needs them (ENV or WASTE)
  const selectedType = permitTypes?.find((t) => t.code === value.permitTypeCode);

  const { data: riskLevels } = useQuery({
    queryKey: ['risk-levels', selectedType?.id],
    queryFn: () =>
      configMgmtControllerListRiskLevels({ id: selectedType!.id }) as unknown as Promise<
        RiskLevel[]
      >,
    enabled: !!selectedType,
  });

  // Sync riskCategoryTotalFee and riskId when riskLevels loads (covers renewal pre-fill case)
  useEffect(() => {
    if (value.riskCode && riskLevels) {
      const risk = riskLevels.find((r) => r.code === value.riskCode);
      if (risk) {
        const fee = Number(risk.totalFee);
        const updates: Partial<PermitDetailsData> = {};
        if (fee !== value.riskCategoryTotalFee) updates.riskCategoryTotalFee = fee;
        if (risk.id !== value.riskId) updates.riskId = risk.id;
        if (Object.keys(updates).length) onChange(updates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskLevels, value.riskCode]);

  const isRenewal = value.requestType === 'RENEWAL';
  const supportsRenewalSearch =
    isRenewal && (value.permitTypeCode === 'ENV' || value.permitTypeCode === 'VEH');

  const { data: renewablePermits } = useQuery({
    queryKey: ['renewable-permits', accountId, value.permitTypeCode],
    queryFn: () =>
      requestsControllerFindAll({
        accountId,
        requestAction: 'NEW',
        status: 'PERMIT_ISSUED',
        permitTypeCode: value.permitTypeCode,
        limit: 100,
      }) as unknown as Promise<OriginalPermitOption[]>,
    enabled: supportsRenewalSearch && !!accountId,
  });

  // Risk levels for ENV, filtered by current permit action
  const filteredRiskLevels = riskLevels?.filter(
    (r) => r.status === 'ACTIVE' && (!value.requestType || r.applyTo === value.requestType),
  );

  // Risk levels for WASTE and VEH (all active, no action filter needed)
  const wasteCategories = riskLevels?.filter((r) => r.status === 'ACTIVE');
  const vehicleCategories = riskLevels?.filter((r) => r.status === 'ACTIVE');

  function selectType(code: string) {
    const pt = permitTypes?.find((t) => t.code === code);
    onChange({
      permitTypeCode: code,
      permitTypeId: pt?.id ?? '',
      requestType: '',
      facilitySubType: '',
      riskCode: '',
      riskId: '',
      riskCategoryTotalFee: 0,
      permitActivity: '',
      envPermitGeneralComments: '',
      licenseType: '',
      isRenewalOfId: '',
      vehicleCount: 1,
      vehicleLicenses: [{ licenseNumber: '', placeOfIssue: '' }],
      wasteClassification: '',
      wasteType: '',
      wasteQuantity: '',
      landfillLocation: '',
    });
  }

  function selectOriginalPermit(id: string) {
    const original = renewablePermits?.find((p) => p.id === id);
    if (!original) {
      onChange({ isRenewalOfId: id });
      return;
    }
    onChange({
      isRenewalOfId: original.id,
      ...(original.riskCode ? { riskCode: original.riskCode, riskCategoryTotalFee: 0 } : {}),
    });
  }

  function addVehicle() {
    const next = [...value.vehicleLicenses, { licenseNumber: '', placeOfIssue: '' }];
    onChange({ vehicleCount: next.length, vehicleLicenses: next });
  }

  function removeVehicle(index: number) {
    const next = value.vehicleLicenses.filter((_, i) => i !== index);
    onChange({ vehicleCount: next.length, vehicleLicenses: next });
  }

  function updateLicense(index: number, field: keyof VehicleLicense, val: string) {
    const next = value.vehicleLicenses.map((l, i) => (i === index ? { ...l, [field]: val } : l));
    onChange({ vehicleLicenses: next });
  }

  return (
    <div className="space-y-6">
      {/* Permit type cards */}
      {!hideTypeCards && (
        <div>
          <Label className="mb-3 block text-sm font-semibold text-gray-700">Permit Type</Label>
          <div className="grid grid-cols-3 gap-3">
            {(permitTypes ?? []).map((pt) => {
              const style = CODE_STYLE[pt.code] ?? DEFAULT_STYLE;
              const Icon = style.icon;
              const selected = value.permitTypeCode === pt.code;
              return (
                <button
                  key={pt.code}
                  type="button"
                  onClick={() => selectType(pt.code)}
                  className={`flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-all ${
                    selected
                      ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-200'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`rounded-lg p-2 ${selected ? style.selectedBg : 'bg-gray-100'}`}>
                    <Icon size={18} className={selected ? 'text-white' : style.iconColor} />
                  </div>
                  <div>
                    <div
                      className={`text-sm font-semibold ${selected ? 'text-brand-700' : 'text-gray-900'}`}
                    >
                      {pt.name}
                    </div>
                    {pt.hasRenewal && (
                      <div className="text-xs text-gray-500 mt-0.5">New &amp; Renewal</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {errors.permitTypeCode && (
            <p className="mt-1 text-xs text-red-500">{errors.permitTypeCode}</p>
          )}
        </div>
      )}

      {/* Environmental fields */}
      {value.permitTypeCode === 'ENV' && (
        <div className="space-y-5 rounded-xl border border-gray-100 bg-gray-50 p-5">
          {/* Row 1: Request Type + Risk Category */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">Request Type</Label>
              <Select
                value={value.requestType || undefined}
                onValueChange={(v) =>
                  onChange({ requestType: v, riskCode: '', riskId: '', riskCategoryTotalFee: 0 })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select request type…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">New Request</SelectItem>
                  <SelectItem value="RENEWAL">Renewal Request</SelectItem>
                </SelectContent>
              </Select>
              {errors.requestType && (
                <p className="mt-1 text-xs text-red-500">{errors.requestType}</p>
              )}
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">Risk Category</Label>
              <Select
                value={value.riskCode || undefined}
                disabled={!value.requestType}
                onValueChange={(v) => {
                  const r = filteredRiskLevels?.find((rl) => rl.code === v);
                  if (r)
                    onChange({
                      riskCode: r.code,
                      riskId: r.id,
                      riskCategoryTotalFee: Number(r.totalFee),
                    });
                }}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      value.requestType ? 'Select risk category…' : 'Select request type first'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(filteredRiskLevels ?? []).map((r) => (
                    <SelectItem key={r.id} value={r.code}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.riskCode && <p className="mt-1 text-xs text-red-500">{errors.riskCode}</p>}
            </div>
          </div>

          {/* Row 2: Permission Date + Expiry Date */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">
                Permission Date <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={value.permissionDate}
                onChange={(v) => onChange({ permissionDate: v })}
                placeholder="Select permission date"
              />
              {errors.permissionDate && (
                <p className="mt-1 text-xs text-red-500">{errors.permissionDate}</p>
              )}
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">
                Expiry Date <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={value.expiryDate}
                onChange={(v) => onChange({ expiryDate: v })}
                placeholder="Select expiry date"
                minDate={expiryMinDate(value.permissionDate)}
              />
              {errors.expiryDate && (
                <p className="mt-1 text-xs text-red-500">{errors.expiryDate}</p>
              )}
            </div>
          </div>

          {/* Renewing permit — conditional */}
          {isRenewal && (
            <div>
              <Label className="mb-2 block text-sm font-medium">Renewing Permit</Label>
              {renewablePermits && renewablePermits.length > 0 ? (
                <Select
                  value={value.isRenewalOfId || undefined}
                  onValueChange={(v) => selectOriginalPermit(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select issued permit to renew…" />
                  </SelectTrigger>
                  <SelectContent>
                    {renewablePermits.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.referenceNo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Enter prior permit ID"
                  value={value.isRenewalOfId}
                  onChange={(e) => onChange({ isRenewalOfId: e.target.value })}
                />
              )}
            </div>
          )}

          {/* Row 3: License Type + Permit Activity */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">License Type</Label>
              <Select
                value={value.licenseType || undefined}
                onValueChange={(v) => onChange({ licenseType: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select license type…" />
                </SelectTrigger>
                <SelectContent>
                  {LICENSE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">Permit Activity</Label>
              <Input
                placeholder="Describe the activity"
                value={value.permitActivity}
                onChange={(e) => onChange({ permitActivity: e.target.value })}
              />
            </div>
          </div>

          {/* General Comments */}
          <div>
            <Label className="mb-2 block text-sm font-medium">General Comments</Label>
            <textarea
              rows={3}
              placeholder="Any additional comments…"
              value={value.envPermitGeneralComments}
              onChange={(e) => onChange({ envPermitGeneralComments: e.target.value })}
              className="w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all resize-none"
            />
          </div>
        </div>
      )}

      {/* Vehicle fields */}
      {value.permitTypeCode === 'VEH' && (
        <div className="space-y-5 rounded-xl border border-gray-100 bg-gray-50 p-5">
          {/* Row 1: Request Type + Category */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">
                Request Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={value.requestType || undefined}
                onValueChange={(v) => onChange({ requestType: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select request type…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">New Request</SelectItem>
                  <SelectItem value="RENEWAL">Renewal Request</SelectItem>
                </SelectContent>
              </Select>
              {errors.requestType && (
                <p className="mt-1 text-xs text-red-500">{errors.requestType}</p>
              )}
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={value.riskCode || undefined}
                onValueChange={(v) => {
                  const r = vehicleCategories?.find((rl) => rl.code === v);
                  if (r)
                    onChange({
                      riskCode: r.code,
                      riskId: r.id,
                      riskCategoryTotalFee: Number(r.totalFee),
                    });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category…" />
                </SelectTrigger>
                <SelectContent>
                  {(vehicleCategories ?? []).map((r) => (
                    <SelectItem key={r.id} value={r.code}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.riskCode && <p className="mt-1 text-xs text-red-500">{errors.riskCode}</p>}
              {value.riskId && (
                <p className="mt-1 text-xs text-gray-500">
                  Category price: AED{' '}
                  {Number(
                    vehicleCategories?.find((r) => r.code === value.riskCode)?.totalFee ?? 0,
                  ).toLocaleString('en-AE', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
          </div>

          {/* Row 2: Permission Date + Expiry Date */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">
                Permission Date <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={value.permissionDate}
                onChange={(v) => onChange({ permissionDate: v })}
                placeholder="Select permission date"
              />
              {errors.permissionDate && (
                <p className="mt-1 text-xs text-red-500">{errors.permissionDate}</p>
              )}
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">Expiry Date</Label>
              <DatePicker
                value={value.expiryDate}
                onChange={(v) => onChange({ expiryDate: v })}
                placeholder="Select expiry date"
                minDate={expiryMinDate(value.permissionDate)}
              />
              {errors.expiryDate && (
                <p className="mt-1 text-xs text-red-500">{errors.expiryDate}</p>
              )}
            </div>
          </div>

          {/* Renewing permit — conditional */}
          {isRenewal && (
            <div>
              <Label className="mb-2 block text-sm font-medium">Renewing Permit</Label>
              {renewablePermits && renewablePermits.length > 0 ? (
                <Select
                  value={value.isRenewalOfId || undefined}
                  onValueChange={(v) => selectOriginalPermit(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select issued permit to renew…" />
                  </SelectTrigger>
                  <SelectContent>
                    {renewablePermits.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.referenceNo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Enter prior permit ID"
                  value={value.isRenewalOfId}
                  onChange={(e) => onChange({ isRenewalOfId: e.target.value })}
                />
              )}
            </div>
          )}

          {/* Vehicle list */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">
              Total Vehicles: {value.vehicleLicenses.length}
            </p>

            {value.vehicleLicenses.map((lic, i) => (
              <div key={i} className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <Label className="mb-1.5 block text-sm font-medium">
                    Vehicle {i + 1} License
                  </Label>
                  <Input
                    placeholder="License plate number"
                    value={lic.licenseNumber}
                    onChange={(e) => updateLicense(i, 'licenseNumber', e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="mb-1.5 block text-sm font-medium">
                      Vehicle {i + 1} Place of Issue
                    </Label>
                    <Select
                      value={lic.placeOfIssue}
                      onValueChange={(v) => updateLicense(i, 'placeOfIssue', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select emirate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                        <SelectItem value="Dubai">Dubai</SelectItem>
                        <SelectItem value="Sharjah">Sharjah</SelectItem>
                        <SelectItem value="Ajman">Ajman</SelectItem>
                        <SelectItem value="Umm Al Quwain">Umm Al Quwain</SelectItem>
                        <SelectItem value="Ras Al Khaimah">Ras Al Khaimah</SelectItem>
                        <SelectItem value="Fujairah">Fujairah</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {value.vehicleLicenses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeVehicle(i)}
                      className="mb-0 mt-auto h-10 w-10 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addVehicle}
              className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-500 hover:border-brand-400 hover:text-brand-500 hover:bg-brand-50 transition-all"
            >
              <Plus size={16} />
              Add More Vehicles
            </button>
          </div>
        </div>
      )}

      {/* Waste fields */}
      {value.permitTypeCode === 'WASTE' && (
        <div className="space-y-5 rounded-xl border border-gray-100 bg-gray-50 p-5">
          {/* Category (required) */}
          <div>
            <Label className="mb-2 block text-sm font-medium">
              Category <span className="text-red-500">*</span>
            </Label>
            <Select
              value={value.riskCode || undefined}
              onValueChange={(v) => {
                const r = wasteCategories?.find((rl) => rl.code === v);
                if (r)
                  onChange({
                    riskCode: r.code,
                    riskId: r.id,
                    riskCategoryTotalFee: Number(r.totalFee),
                  });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category…" />
              </SelectTrigger>
              <SelectContent>
                {(wasteCategories ?? []).map((r) => (
                  <SelectItem key={r.id} value={r.code}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.riskCode && <p className="mt-1 text-xs text-red-500">{errors.riskCode}</p>}
            {value.riskId && (
              <p className="mt-1 text-xs text-gray-500">
                Category price: AED{' '}
                {Number(
                  wasteCategories?.find((r) => r.code === value.riskCode)?.totalFee ?? 0,
                ).toLocaleString('en-AE', { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>

          {/* Permit Date + Expiry Date */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">
                Permit Date <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={value.permissionDate}
                onChange={(v) => onChange({ permissionDate: v })}
                placeholder="Select permit date"
              />
              {errors.permissionDate && (
                <p className="mt-1 text-xs text-red-500">{errors.permissionDate}</p>
              )}
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">Expiry Date</Label>
              <DatePicker
                value={value.expiryDate}
                onChange={(v) => onChange({ expiryDate: v })}
                placeholder="Select expiry date"
                minDate={expiryMinDate(value.permissionDate)}
              />
              {errors.expiryDate && (
                <p className="mt-1 text-xs text-red-500">{errors.expiryDate}</p>
              )}
            </div>
          </div>

          {/* Permit Activity */}
          <div>
            <Label className="mb-2 block text-sm font-medium">Permit Activity</Label>
            <Input
              placeholder="Describe the activity"
              value={value.permitActivity}
              onChange={(e) => onChange({ permitActivity: e.target.value })}
            />
          </div>

          {/* Waste Classification + Type of Waste */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">Waste Classification</Label>
              <Input
                placeholder="e.g. Hazardous, Non-hazardous"
                value={value.wasteClassification}
                onChange={(e) => onChange({ wasteClassification: e.target.value })}
              />
            </div>
            <div>
              <Label className="mb-2 block text-sm font-medium">Type of Waste</Label>
              <Input
                placeholder="e.g. Chemical, Medical"
                value={value.wasteType}
                onChange={(e) => onChange({ wasteType: e.target.value })}
              />
            </div>
          </div>

          {/* Quantity of Waste + Landfill Location */}
          <div className="grid grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block text-sm font-medium">Quantity of Waste</Label>
              <Input
                type="number"
                min={0}
                step="any"
                placeholder="Enter quantity"
                value={value.wasteQuantity}
                onChange={(e) => onChange({ wasteQuantity: e.target.value })}
              />
              {errors.wasteQuantity && (
                <p className="mt-1 text-xs text-red-500">{errors.wasteQuantity}</p>
              )}
            </div>
            <div>
              <Label className="mb-2 block text-sm font-medium">Landfill Location</Label>
              <Input
                placeholder="Enter landfill location"
                value={value.landfillLocation}
                onChange={(e) => onChange({ landfillLocation: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
